import * as React from 'react'

import { Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import OperationRule from '~/client/src/shared/models/OperationRule'

import { NotificationSetupModes } from '../../../../NotificationsSetup'
import BaseNotificationRulesStore from '../../BaseNotificationRules.store'
import ActionPicker from '../ActionPicker/ActionPicker'
import ExpressionControl from '../ExpressionControl/ExpressionControl'
import RuleRowStore from './RuleRow.store'

import './RuleRow.scss'

// translated

interface IProps {
  rule: OperationRule
  onRemoveRule: (rule: OperationRule) => void
  onAddAssignmentPlaceholderRow: (rule: OperationRule) => void
  mode: NotificationSetupModes
  onSaveChanges: (rule: OperationRule, shouldSendRequest?: boolean) => void

  measureSize: () => void

  notificationRulesStore: BaseNotificationRulesStore
}

@observer
export default class RuleRow extends React.PureComponent<IProps> {
  private readonly store: RuleRowStore = null

  public constructor(props: IProps) {
    super(props)
    this.store = new RuleRowStore(
      props.onRemoveRule,
      props.onAddAssignmentPlaceholderRow,
      props.onSaveChanges,
      props.measureSize,
      props.rule,
    )
  }

  public componentDidUpdate() {
    const { rule } = this.props

    if (rule !== this.store.rule) {
      this.store.reInit(rule)
      return
    }
    this.store.measureSize()
  }

  public componentDidMount() {
    if (this.props.measureSize) {
      requestAnimationFrame(() => {
        this.props.measureSize()
      })
    }
  }

  public render() {
    const {
      isHovering,
      rule: { assignmentsAsSubexpressions },
      handleMouseEnter,
      handleMouseLeave,
    } = this.store

    return (
      <div
        className="rule-row-container tw:flex tw:items-start"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {this.renderTrashIcon(isHovering)}
        {this.renderExpressionControl()}

        <div className="col no-grow">
          {assignmentsAsSubexpressions.map((expression, i) => {
            const isFirstSubLine = i === 0
            const shouldShowDeleteIcon =
              assignmentsAsSubexpressions.length > 1 || !!expression

            return (
              <div
                key={expression}
                className="row full-height align-start actions-users-container"
              >
                <div className="row no-grow full-height relative action-picker-col">
                  {isFirstSubLine && this.renderActionPicker()}
                </div>
                <div className="row no-grow full-height px12 user-picker-col">
                  {!this.store.isDisabledAssignment &&
                    this.renderAssignmentExpression(
                      expression,
                      shouldShowDeleteIcon,
                    )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  private renderTrashIcon(isHovering: boolean): JSX.Element {
    if (!isHovering) {
      return null
    }

    return (
      <Button
        variant="destructive-ghost"
        size="icon"
        className="tw:self-center"
        aria-label={Localization.translator.delete}
        onClick={this.store.removeRule}
      >
        <Trash2Icon />
      </Button>
    )
  }

  private renderExpressionControl(): JSX.Element {
    const { rule, changeExpression } = this.store
    const { measureSize, notificationRulesStore, mode } = this.props
    const availableTags = notificationRulesStore.getAvailableTags(
      rule.operationSubject,
    )

    return (
      <div className="row x-start align-start relative tw:min-w-0 tw:flex-1">
        <ExpressionControl
          expression={rule.expression}
          subject={rule.operationSubject}
          onChange={changeExpression}
          shouldContainsTagsSelectorHandler={true}
          availableTags={availableTags}
          isElementsRemovable={true}
          measureSize={measureSize}
          mode={mode}
        />
      </div>
    )
  }

  private renderActionPicker(): JSX.Element {
    return (
      <ActionPicker
        mode={this.props.mode}
        projectId={this.props.rule.projectId}
        operationRuleId={this.props.rule.id}
        handleRuleTypeChange={this.store.changeOperationType}
        operationType={this.props.rule.operation}
      />
    )
  }

  private renderAssignmentExpression(
    expression: string,
    shouldShowDeleteIcon: boolean,
  ) {
    const {
      selectAssigneeSubexpression,
      removeAssigneeSubexpression,
      clickOnExpressionPlusControl,
    } = this.store
    const { measureSize, notificationRulesStore } = this.props
    return (
      <ExpressionControl
        expression={expression}
        isElementsRemovable={true}
        shouldContainsDeleteIcon={shouldShowDeleteIcon}
        shouldUseTagsSelector={true}
        availableTags={notificationRulesStore.userRelatedTags}
        onDeleteExpression={removeAssigneeSubexpression}
        onChange={selectAssigneeSubexpression.bind(null, expression)}
        onPlusControlCustomClick={clickOnExpressionPlusControl}
        measureSize={measureSize}
        mode={this.props.mode}
      />
    )
  }
}
